/**
 * V1.9.3 — 3D extrusion of floor-plan symbols.
 *
 * Reads `floor.symbols` (the array populated by the 2D Symbol Palette)
 * and emits low-poly extruded boxes / category-appropriate primitives
 * into the floor's Three.js group. World coordinates map 1:1 with
 * the BIM rooms (no axis flip).
 *
 * Each entry: { id, type, x, z, rotation_deg, override_w_m?, override_d_m? }
 *   - (x, z) is the symbol's TOP-LEFT corner in metres (same as rooms).
 *   - rotation_deg rotates around the symbol centre, CCW in plan view.
 *
 * We honour the symbol catalog's default width/depth but pick a sensible
 * height per category + per-slug override (e.g. tall fridges, low coffee
 * tables, multi-step stairs).
 */
import * as THREE from "three";
import { SYMBOL_CATALOG } from "../lib/symbols";

// Default extrusion height per category (metres).
const CATEGORY_HEIGHT_M = {
  furniture: 0.45,
  appliance: 0.90,
  plumbing: 0.45,
  stairs: 1.20,
  misc: 2.10,
};

// Per-slug overrides — overrides win over the category default.
const SLUG_HEIGHT_M = {
  // Furniture
  bed_single: 0.55, bed_double: 0.55, bed_king: 0.55,
  sofa: 0.85, chair_single: 0.95,
  coffee_table: 0.42, end_table: 0.52,
  desk: 0.75, dressing_table: 0.78,
  // Appliances
  refrigerator: 1.80, stove_oven: 0.90, dishwasher: 0.85,
  washer: 0.95, dryer: 0.95,
  sink_single: 0.92, sink_double: 0.92,
  cabinet: 0.90, countertop: 0.92,
  // Plumbing
  toilet: 0.42, bidet: 0.40, lavatory: 0.85,
  bathtub: 0.55, bathtub_corner: 0.55,
  shower_stall: 2.05, shower_corner: 2.05,
  // Misc
  closet: 2.35,
};

// Pastel + grounded category colours that read well in the 3D scene.
const CATEGORY_COLOR = {
  furniture: 0xb08968, // warm tan
  appliance: 0xb0b3b8, // cool grey
  plumbing: 0xa7c4d8, // soft blue
  stairs:   0xc69b6d, // amber wood
  misc:     0x8a9b75, // olive
};

// Shared material cache so 50 beds don't allocate 50 materials.
const matCache = new Map();
function getMaterial(color) {
  if (matCache.has(color)) return matCache.get(color);
  const m = new THREE.MeshStandardMaterial({
    color, roughness: 0.78, metalness: 0.08,
  });
  matCache.set(color, m);
  return m;
}

function heightForSymbol(slug, category) {
  if (SLUG_HEIGHT_M[slug]) return SLUG_HEIGHT_M[slug];
  return CATEGORY_HEIGHT_M[category] || 0.5;
}

// Build a stepped stairs primitive — 8 small risers stacked into a wedge.
function buildStairs(w, d, h, color) {
  const stairsGroup = new THREE.Group();
  const STEP_COUNT = 8;
  const stepRise = h / STEP_COUNT;
  const stepRun = d / STEP_COUNT;
  const mat = getMaterial(color);
  for (let i = 0; i < STEP_COUNT; i += 1) {
    const stepGeom = new THREE.BoxGeometry(w, stepRise, stepRun);
    const stepMesh = new THREE.Mesh(stepGeom, mat);
    stepMesh.castShadow = true;
    stepMesh.receiveShadow = true;
    // Origin: bottom-front of the lowest step at (0, 0, 0). We'll translate
    // the entire group later. Each step climbs by stepRise and recedes by
    // stepRun. The local origin is the centre of the stairs footprint.
    stepMesh.position.set(
      0,
      stepRise / 2 + i * stepRise,
      -d / 2 + stepRun / 2 + i * stepRun,
    );
    stairsGroup.add(stepMesh);
  }
  return stairsGroup;
}

// Build a single symbol mesh (Box for furniture/appliances/plumbing,
// stepped stairs for stairs, tall box for misc).
function buildSymbolMesh(sym) {
  const cat = SYMBOL_CATALOG[sym.type];
  if (!cat) return null;
  const w = Math.max(0.05, Number(sym.override_w_m) || cat.width_m);
  const d = Math.max(0.05, Number(sym.override_d_m) || cat.depth_m);
  // V1.9.6 — per-symbol height override; falls back to the per-slug or
  // per-category default. Clamped to [0.05, 6] m to prevent absurd values.
  const overrideH = Number(sym.override_h_m);
  const baseH = heightForSymbol(sym.type, cat.category);
  const h = Number.isFinite(overrideH) && overrideH > 0
    ? Math.min(6, Math.max(0.05, overrideH))
    : baseH;
  const color = CATEGORY_COLOR[cat.category] || 0x9aa0a6;

  if (cat.category === "stairs") {
    const stairs = buildStairs(w, d, h, color);
    stairs.userData = { kind: "symbol", id: sym.id, slug: sym.type };
    return stairs;
  }

  const geom = new THREE.BoxGeometry(w, h, d);
  const mesh = new THREE.Mesh(geom, getMaterial(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData = { kind: "symbol", id: sym.id, slug: sym.type };
  // Box is centred at its origin, lift it so its base sits on the floor.
  mesh.position.y = h / 2;
  return mesh;
}

/**
 * Build all symbols for a floor and add them to `floorGroup`.
 * `floorGroup` is already translated to the floor's `height_offset`,
 * so symbol y=0 sits on this floor's slab.
 */
export function buildSymbolsForFloor(floorGroup, floor) {
  const symbols = Array.isArray(floor?.symbols) ? floor.symbols : [];
  if (symbols.length === 0) return;
  symbols.forEach((sym) => {
    if (!sym || typeof sym.x !== "number" || typeof sym.z !== "number") return;
    const node = buildSymbolMesh(sym);
    if (!node) return;
    const cat = SYMBOL_CATALOG[sym.type];
    const w = Math.max(0.05, Number(sym.override_w_m) || cat.width_m);
    const d = Math.max(0.05, Number(sym.override_d_m) || cat.depth_m);
    // The symbol's stored (x, z) is the top-left corner; the mesh local
    // origin is the centre of the footprint. Translate to the centre.
    const wrap = new THREE.Group();
    wrap.userData = { kind: "symbol-wrap", id: sym.id, slug: sym.type };
    wrap.position.set(sym.x + w / 2, 0, sym.z + d / 2);
    // Rotation in plan view: CCW in screen-space → CW around the Y axis
    // in Three.js (because Three.js +Z is "down" in the 2D top-down).
    wrap.rotation.y = -((Number(sym.rotation_deg) || 0) * Math.PI) / 180;
    wrap.add(node);
    floorGroup.add(wrap);
  });
}
